import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { BottomNav } from './components/BottomNav';
import { DiscoverView } from './components/DiscoverView';
import { MatchesView } from './components/MatchesView';
import { MessagesView } from './components/MessagesView';
import { ProfileView } from './components/ProfileView';
import { ChatView } from './components/ChatView';
import { MOCK_PROFILES, CURRENT_USER, MOCK_MATCHES, MOCK_THREADS } from './data/mockData';
import { Thread, Match, User } from './types';
type Tab = 'discover' | 'matches' | 'messages' | 'profile';
export function App() {
  const [activeTab, setActiveTab] = useState<Tab>('discover');
  const [activeThread, setActiveThread] = useState<Thread | null>(null);
  const [showMatchPopup, setShowMatchPopup] = useState<User | null>(null);
  const handleMatch = (user: User) => {
    setShowMatchPopup(user);
    setTimeout(() => setShowMatchPopup(null), 4000);
  };
  const handleSelectThread = (thread: Thread) => {
    setActiveThread(thread);
  };
  const handleSelectMatch = (match: Match) => {
    const existingThread = MOCK_THREADS.find(t => t.matchId === match.id);
    if (existingThread) {
      setActiveThread(existingThread);
    }
  };
  const renderContent = () => {
    switch (activeTab) {
      case 'discover':
        return <DiscoverView profiles={MOCK_PROFILES} onMatch={handleMatch} data-id="element-0" />;
      case 'matches':
        return <MatchesView matches={MOCK_MATCHES} onSelectMatch={handleSelectMatch} data-id="element-1" />;
      case 'messages':
        return <MessagesView threads={MOCK_THREADS} onSelectThread={handleSelectThread} data-id="element-2" />;
      case 'profile':
        return <ProfileView user={CURRENT_USER} data-id="element-3" />;
      default:
        return <DiscoverView profiles={MOCK_PROFILES} onMatch={handleMatch} data-id="element-4" />;
    }
  };
  return <div className="min-h-screen bg-zinc-100 flex items-center justify-center font-sans text-zinc-900" data-id="element-5">
      {/* Mobile Container */}
      <div className="w-full h-[100dvh] md:h-[850px] md:max-w-[400px] bg-white md:rounded-[40px] shadow-2xl overflow-hidden relative flex flex-col" data-id="element-6">
        {/* Main Content Area */}
        <main className="flex-1 overflow-hidden relative bg-zinc-50" data-id="element-7">
          <AnimatePresence mode="wait" data-id="element-8">
            {activeThread ? <motion.div key="chat" initial={{
            x: '100%'
          }} animate={{
            x: 0
          }} exit={{
            x: '100%'
          }} transition={{
            type: 'spring',
            damping: 25,
            stiffness: 200
          }} className="absolute inset-0 z-40" data-id="element-9">
                <ChatView thread={activeThread} onBack={() => setActiveThread(null)} data-id="element-10" />
              </motion.div> : <motion.div key={activeTab} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className="h-full" data-id="element-11">
                {renderContent()}
              </motion.div>}
          </AnimatePresence>
        </main>

        {/* Navigation - Hidden when in chat */}
        {!activeThread && <BottomNav activeTab={activeTab} onTabChange={setActiveTab} unreadMatches={1} unreadMessages={2} data-id="element-12" />}

        {/* Spark Match Popup */}
        <AnimatePresence data-id="element-13">
          {showMatchPopup && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 z-50 flex items-center justify-center bg-gradient-to-b from-orange-500/90 to-rose-600/90 backdrop-blur-md p-8" onClick={() => setShowMatchPopup(null)} data-id="element-14">
              {/* Sparkle Effects */}
              <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-15">
                {[...Array(20)].map((_, i) => <motion.div key={i} initial={{
              opacity: 0,
              scale: 0,
              x: Math.random() * 400 - 200,
              y: Math.random() * 600 - 300
            }} animate={{
              opacity: [0, 1, 0],
              scale: [0, 1, 0]
            }} transition={{
              duration: 2,
              delay: Math.random() * 2,
              repeat: Infinity,
              repeatDelay: Math.random() * 2
            }} className="absolute top-1/2 left-1/2" data-id="element-16">
                    <Sparkles size={16 + Math.random() * 16} className="text-white/60" data-id="element-17" />
                  </motion.div>)}
              </div>

              <motion.div initial={{
            scale: 0.5,
            y: 50
          }} animate={{
            scale: 1,
            y: 0
          }} transition={{
            type: 'spring',
            damping: 15,
            stiffness: 200,
            delay: 0.1
          }} className="text-center relative z-10" data-id="element-18">
                {/* Logo */}
                <motion.div initial={{
              rotate: -180,
              scale: 0
            }} animate={{
              rotate: 0,
              scale: 1
            }} transition={{
              type: 'spring',
              damping: 10,
              stiffness: 100,
              delay: 0.2
            }} className="flex justify-center mb-4" data-id="element-19">
                  <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-2xl" data-id="element-20">
                    <Sparkles size={36} className="text-rose-500" data-id="element-21" />
                  </div>
                </motion.div>

                <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="text-4xl font-black text-white mb-2 tracking-tight" data-id="element-22">
                  It's a Spark!
                </motion.h2>

                <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.4
            }} className="text-white/80 mb-8" data-id="element-23">
                  You and {showMatchPopup.name} like each other
                </motion.p>

                <motion.div initial={{
              opacity: 0,
              scale: 0.8
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              delay: 0.5
            }} className="flex justify-center items-center gap-6 mb-10" data-id="element-24">
                  <div className="relative" data-id="element-25">
                    <div className="w-28 h-28 rounded-full border-4 border-white overflow-hidden shadow-2xl" data-id="element-26">
                      <img src={CURRENT_USER.photos[0]} alt="Me" className="w-full h-full object-cover" data-id="element-27" />
                    </div>
                    <motion.div animate={{
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 1,
                  repeat: Infinity
                }} className="absolute -bottom-1 -right-1 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-28">
                      <Sparkles size={16} className="text-rose-500" data-id="element-29" />
                    </motion.div>
                  </div>
                  <div className="relative" data-id="element-30">
                    <div className="w-28 h-28 rounded-full border-4 border-white overflow-hidden shadow-2xl" data-id="element-31">
                      <img src={showMatchPopup.photos[0]} alt="Match" className="w-full h-full object-cover" data-id="element-32" />
                    </div>
                    <motion.div animate={{
                  scale: [1, 1.2, 1]
                }} transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: 0.5
                }} className="absolute -bottom-1 -left-1 w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg" data-id="element-33">
                      <Sparkles size={16} className="text-rose-500" data-id="element-34" />
                    </motion.div>
                  </div>
                </motion.div>

                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.6
            }} className="space-y-3" data-id="element-35">
                  <button onClick={() => setShowMatchPopup(null)} className="w-full py-4 bg-white text-rose-500 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-shadow" data-id="element-36">
                    Send a Message
                  </button>

                  <button onClick={() => setShowMatchPopup(null)} className="w-full py-3 text-white/90 font-medium hover:text-white transition-colors" data-id="element-37">
                    Keep Swiping
                  </button>
                </motion.div>
              </motion.div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}